import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import {
    Plus,
    Search,
    FileText,
    Trash2,
    Download,
    Eye,
    ArrowRightLeft,
    Loader2,
} from "lucide-react";
import { cn, getStatusColor, statusLabels, formatDate, formatCurrency } from "../../lib/utils";
import { QuotePDFTemplate } from "../../components/pdf/PDFTemplates";
import { Pagination } from "../../components/ui/pagination-control";

export const QuotesPage = () => {
    const navigate = useNavigate();
    const { get, post, put } = useApi();
    const [quotes, setQuotes] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tenant, setTenant] = useState(null);
    const [billingSettings, setBillingSettings] = useState(null);
    const [quoteSettings, setQuoteSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [convertingId, setConvertingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 15;
    const [formData, setFormData] = useState({
        client_id: "",
        project_id: "",
        items: [{ description: "", quantity: 1, unit_price: 0 }],
        discount_percent: 0,
        notes: "",
    });

    const fetchData = useCallback(async () => {
        const results = await Promise.allSettled([
            get("/quotes"),
            get("/clients"),
            get("/projects"),
            get("/settings/tenant"),
            get("/settings/billing"),
            get("/settings/quotes"),
        ]);
        if (results[0].status === "fulfilled") setQuotes(results[0].value);
        else toast.error("Erreur lors du chargement des devis");
        if (results[1].status === "fulfilled") setClients(results[1].value);
        if (results[2].status === "fulfilled") setProjects(results[2].value);
        if (results[3].status === "fulfilled") setTenant(results[3].value);
        if (results[4].status === "fulfilled") setBillingSettings(results[4].value);
        if (results[5].status === "fulfilled") setQuoteSettings(results[5].value);
        setLoading(false);
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openPdfDialog = (quote) => {
        setSelectedQuote(quote);
        setPdfDialogOpen(true);
    };

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, { description: "", quantity: 1, unit_price: 0 }],
        });
    };

    const removeItem = (index) => {
        setFormData({
            ...formData,
            items: formData.items.filter((_, i) => i !== index),
        });
    };

    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData({ ...formData, items: newItems });
    };

    const calculateTotal = () => {
        const subtotal = formData.items.reduce(
            (sum, item) => sum + item.quantity * item.unit_price,
            0
        );
        const discount = subtotal * (formData.discount_percent / 100);
        const tax = (subtotal - discount) * 0.2;
        return {
            subtotal,
            discount,
            tax,
            total: subtotal - discount + tax,
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.client_id) {
            toast.error("Veuillez sélectionner un client");
            return;
        }
        if (formData.items.length === 0 || !formData.items[0].description) {
            toast.error("Veuillez ajouter au moins un article");
            return;
        }

        setSubmitting(true);
        try {
            await post("/quotes", {
                ...formData,
                project_id: formData.project_id || null,
            });
            toast.success("Devis créé avec succès");
            setDialogOpen(false);
            setFormData({
                client_id: "",
                project_id: "",
                items: [{ description: "", quantity: 1, unit_price: 0 }],
                discount_percent: 0,
                notes: "",
            });
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la création du devis");
        } finally {
            setSubmitting(false);
        }
    };

    const updateStatus = async (quoteId, status) => {
        try {
            await put(`/quotes/${quoteId}/status?status=${status}`);
            toast.success("Statut mis à jour");
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const convertToInvoice = async (quoteId) => {
        setConvertingId(quoteId);
        try {
            const result = await post(`/quotes/${quoteId}/convert-to-invoice`);
            toast.success(`Devis converti en facture ${result.invoice_number}`);
            fetchData();
        } catch (error) {
            toast.error(error.message || "Erreur lors de la conversion");
        } finally {
            setConvertingId(null);
        }
    };

    const filteredQuotes = quotes.filter(
        (quote) =>
            quote.quote_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            quote.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalPages = Math.ceil(filteredQuotes.length / PAGE_SIZE);
    const paginatedQuotes = filteredQuotes.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const totals = calculateTotal();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="quotes-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Devis</h1>
                    <p className="text-muted-foreground mt-1">{quotes.length} devis au total</p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-quote-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau devis
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Nouveau devis</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Client *</Label>
                                    <Select
                                        value={formData.client_id}
                                        onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                                    >
                                        <SelectTrigger data-testid="quote-client-select">
                                            <SelectValue placeholder="Sélectionner un client" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {clients.map((client) => (
                                                <SelectItem key={client.client_id} value={client.client_id}>
                                                    {client.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Projet (optionnel)</Label>
                                    <Select
                                        value={formData.project_id}
                                        onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Sélectionner un projet" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {projects.map((project) => (
                                                <SelectItem key={project.project_id} value={project.project_id}>
                                                    {project.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Items */}
                            <div>
                                <Label className="mb-2 block">Articles</Label>
                                <div className="space-y-2">
                                    {formData.items.map((item, index) => (
                                        <div key={index} className="flex gap-2 items-end">
                                            <div className="flex-1">
                                                <Input
                                                    placeholder="Description"
                                                    value={item.description}
                                                    onChange={(e) => updateItem(index, "description", e.target.value)}
                                                    data-testid={`item-description-${index}`}
                                                />
                                            </div>
                                            <div className="w-24">
                                                <Input
                                                    type="number"
                                                    placeholder="Qté"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                                                    data-testid={`item-quantity-${index}`}
                                                />
                                            </div>
                                            <div className="w-32">
                                                <Input
                                                    type="number"
                                                    placeholder="Prix unitaire"
                                                    value={item.unit_price}
                                                    onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                                                    data-testid={`item-price-${index}`}
                                                />
                                            </div>
                                            <div className="w-28 text-right font-mono text-sm">
                                                {formatCurrency(item.quantity * item.unit_price)}
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeItem(index)}
                                                disabled={formData.items.length === 1}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2">
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter un article
                                </Button>
                            </div>

                            {/* Totals */}
                            <div className="border-t pt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>Sous-total</span>
                                    <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <span>Remise</span>
                                        <Input
                                            type="number"
                                            className="w-20 h-8"
                                            value={formData.discount_percent}
                                            onChange={(e) =>
                                                setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })
                                            }
                                        />
                                        <span>%</span>
                                    </div>
                                    <span className="font-mono">-{formatCurrency(totals.discount)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span>TVA (20%)</span>
                                    <span className="font-mono">{formatCurrency(totals.tax)}</span>
                                </div>
                                <div className="flex justify-between text-lg font-bold border-t pt-2">
                                    <span>Total TTC</span>
                                    <span className="font-mono">{formatCurrency(totals.total)}</span>
                                </div>
                            </div>

                            <div>
                                <Label>Notes</Label>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={2}
                                />
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="submit-quote-btn">
                                    {submitting ? "Création..." : "Créer le devis"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Rechercher un devis..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-quotes-input"
                />
            </div>

            {/* Quotes Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>N° Devis</TableHead>
                                <TableHead>Client</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Montant</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead>PDF</TableHead>
                                <TableHead>Convertir</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedQuotes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                        Aucun devis trouvé
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedQuotes.map((quote) => (
                                    <TableRow key={quote.quote_id} data-testid={`quote-row-${quote.quote_id}`}>
                                        <TableCell className="font-mono font-medium">{quote.quote_number}</TableCell>
                                        <TableCell>{quote.client_name || "-"}</TableCell>
                                        <TableCell>{formatDate(quote.created_at)}</TableCell>
                                        <TableCell className="text-right font-mono">{formatCurrency(quote.total)}</TableCell>
                                        <TableCell>
                                            <Badge className={getStatusColor(quote.status)}>
                                                {statusLabels[quote.status] || quote.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openPdfDialog(quote)}
                                                data-testid={`pdf-btn-${quote.quote_id}`}
                                            >
                                                <Download className="w-4 h-4 mr-1" />
                                                PDF
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            {quote.status === "accepted" && !quote.converted_to_invoice && (
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={() => convertToInvoice(quote.quote_id)}
                                                    disabled={convertingId === quote.quote_id}
                                                    data-testid={`convert-btn-${quote.quote_id}`}
                                                >
                                                    {convertingId === quote.quote_id ? (
                                                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                    ) : (
                                                        <ArrowRightLeft className="w-4 h-4 mr-1" />
                                                    )}
                                                    Facturer
                                                </Button>
                                            )}
                                            {quote.converted_to_invoice && (
                                                <Badge variant="outline" className="text-xs">
                                                    Converti
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Select
                                                value={quote.status}
                                                onValueChange={(value) => updateStatus(quote.quote_id, value)}
                                            >
                                                <SelectTrigger className="w-32 h-8">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="draft">Brouillon</SelectItem>
                                                    <SelectItem value="sent">Envoyé</SelectItem>
                                                    <SelectItem value="accepted">Accepté</SelectItem>
                                                    <SelectItem value="rejected">Rejeté</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* PDF Export Dialog */}
            <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Export PDF - {selectedQuote?.quote_number}</DialogTitle>
                    </DialogHeader>
                    {selectedQuote && (
                        <QuotePDFTemplate
                            quote={selectedQuote}
                            tenant={tenant}
                            billingSettings={billingSettings}
                            quoteSettings={quoteSettings}
                            onExport={() => {
                                toast.success("PDF téléchargé avec succès");
                                setPdfDialogOpen(false);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Pagination
                page={currentPage}
                pages={totalPages}
                total={filteredQuotes.length}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => setCurrentPage(p)}
            />
        </div>
    );
};
